// The transfer motion (#6772, epic #6552): what the Fleet page draws the moment a move is
// ACCEPTED. The caller plays it synchronously on the 201, never on a timer and never on a
// failure. Prototype `docs/design/fleet-redesign/prototype.html`: outbound `:3032-3044`, the
// link ends `:2934-2939`, the beam `:2953-2986`, the dissolve `:2987-3002`, the card warp
// `:3012-3029`, the chip `:3045-3050`.
//
// For each of the first six items, 80 ms apart: the row's echo dissolves in the direction of
// travel, an arc draws from the row to the target card (the first one thicker), a packet runs
// it shedding sparks and lands in two rings. The target card gets corner brackets with a tag,
// a scan line, a glow and a pop on its counts; the source card a short pulse. A target that
// is off screen gets its arcs aimed 40 px below the viewport and a chip naming it flies there.
//
// COLOUR LIVES IN THE STYLESHEET (`styles/views/_machine-move-fx.scss`): this file sets
// classes, geometry and timing only. The one exception is the dissolve's split text shadow,
// which a keyframe has to carry: it names the `--move-fx-split-*` tokens, never a colour.
//
// REDUCED MOTION DRAWS NOTHING: no element, no animation, no timer. The question is asked
// through the seam, whose browser half reads `prefersReducedMotion` (the only reader).
//
// Everything it starts is tracked, and `dispose` undoes all of it: every animation, the one
// timer (the layer's removal at `TRANSFER.layerMs`), every frame, and every element added.

import { prefersReducedMotion } from './reduced-motion';

/** The motion's timing table. The landing numbers are for the caller's landing rows. */
export const TRANSFER = {
  maxArcs: 6,
  staggerMs: 80,
  dissolve: {
    ms: 480,
    ease: 'cubic-bezier(0.5,0,0.75,0)',
    brightness: 2.6,
    splitPx: 5,
    nudgePx: 12,
  },
  arc: { ms: 1050, ease: 'cubic-bezier(0.65,0,0.35,1)' },
  packetMs: 520,
  sparkEveryMs: 14,
  ringMs: 560,
  secondRingMs: 110,
  chipMs: 1200,
  arriveMs: 460,
  brackets: { fromMs: 200, ms: 1150 },
  scanMs: 950,
  glowMs: 1150,
  pop: { ms: 520, afterMs: 120, scale: 1.12 },
  sourcePulseMs: 640,
  layerMs: 2200,
  landingMs: 600,
  landingAfterArcMs: 500,
  landingEase: 'cubic-bezier(0.16,1,0.3,1)',
  tintMs: 2200,
  movingMinMs: 1100,
  movedMs: 2300,
} as const;

const EASE_OUT = 'cubic-bezier(0.16,1,0.3,1)';
const EASE_SWEEP = 'cubic-bezier(0.45,0,0.2,1)';
const SVG_NS = 'http://www.w3.org/2000/svg';
/** How far below the viewport an off-screen target's arcs end. */
const OFFSCREEN_DROP = 40;

/** A box in viewport pixels (a `DOMRect`'s fields). */
export type TransferBox = {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly width: number;
  readonly height: number;
};

export type Point = { readonly x: number; readonly y: number };

/** One planned arc: a cubic bezier from a row to the target. */
export type TransferArc = {
  readonly from: Point;
  readonly c1: Point;
  readonly c2: Point;
  readonly to: Point;
  /** `x` side by side, `y` stacked. */
  readonly axis: 'x' | 'y';
  /** +1 towards right / down, -1 towards left / up. */
  readonly dir: 1 | -1;
  readonly delayMs: number;
  /** The first item's arc: drawn thicker. */
  readonly main: boolean;
};

/** What a move hands the motion. `tag` and `chip` arrive translated. */
export type TransferSpec = {
  /** The moved rows, in order; `node` is the row to echo (null: no echo). */
  readonly rows: readonly { readonly node: Element | null; readonly rect: TransferBox }[];
  readonly source: Element | null;
  readonly target: Element | null;
  readonly targetRect: TransferBox | null;
  readonly viewport: { readonly width: number; readonly height: number };
  /** The brackets' tag on the target card ("Receiving 9 items from box-one"). */
  readonly tag: string;
  /** The chip flown towards an off-screen target ("→ box-two"). */
  readonly chip: string;
};

/** A running animation, cancellable. */
export type MotionHandle = { cancel(): void };

/** Everything the motion touches in the browser, injectable. */
export type MotionSeam = {
  reduced(): boolean;
  animate(el: Element, frames: Keyframe[], opts: KeyframeAnimationOptions): MotionHandle;
  later(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
  frame(fn: () => void): number;
  cancelFrame(handle: number): void;
  now(): number;
  root(): HTMLElement;
};

/** A played transfer. A double dispose is safe. */
export type TransferPlay = { dispose(): void };

const NOOP: TransferPlay = { dispose: () => {} };

/** A cancel's rejection is expected; any other reason is a real failure and is rethrown. */
const expectAbort = (reason: unknown): void => {
  if ((reason as { readonly name?: unknown } | null)?.name !== 'AbortError') throw reason;
};

/**
 * A Web Animation, its promises observed. Cancelling one (every `dispose`, and every unmount
 * mid-motion) rejects its `finished` and a pending `ready` with an AbortError; nothing here
 * awaits them, so unobserved they would surface as unhandled rejections (#6772 round 5).
 */
function observed(a: Animation): MotionHandle {
  a.finished.catch(expectAbort);
  a.ready.catch(expectAbort);
  return a;
}

/** The browser's seam. A browser without `Element.animate` animates nothing. */
export function browserMotionSeam(): MotionSeam {
  return {
    reduced: () => prefersReducedMotion(),
    animate: (el, frames, opts) =>
      typeof el.animate === 'function' ? observed(el.animate(frames, opts)) : { cancel: () => {} },
    later: (fn, ms) => setTimeout(fn, ms),
    clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    frame: (fn) => requestAnimationFrame(() => fn()),
    cancelFrame: (h) => cancelAnimationFrame(h),
    now: () => performance.now(),
    root: () => document.body,
  };
}

// ─── Geometry (pure) ─────────────────────────────────────────────────────────

/** The box an off-screen target stands in for: under the row, below the viewport. */
function offscreenBox(row: TransferBox, viewportHeight: number): TransferBox {
  const top = viewportHeight + OFFSCREEN_DROP;
  return { left: row.left, right: row.right, width: row.width, top, bottom: top + 20, height: 20 };
}

/** True when a box shows any part of itself in the viewport's height. */
function onScreen(box: TransferBox | null, viewportHeight: number): box is TransferBox {
  return box !== null && box.bottom > 0 && box.top < viewportHeight;
}

type Ends = {
  readonly from: Point;
  readonly to: Point;
  readonly axis: 'x' | 'y';
  readonly dir: 1 | -1;
};

/** Where an arc leaves the row and lands on the target (prototype `:2934-2939`). */
function linkEnds(o: TransferBox, n: TransferBox): Ends {
  const oy = o.top + o.height / 2;
  const ny = n.top + n.height / 2;
  if (n.left >= o.right - 4) {
    return { from: { x: o.right - 8, y: oy }, to: { x: n.left + 8, y: ny }, axis: 'x', dir: 1 };
  }
  if (n.right <= o.left + 4) {
    return { from: { x: o.left + 8, y: oy }, to: { x: n.right - 8, y: ny }, axis: 'x', dir: -1 };
  }
  const down = n.top > o.top;
  return {
    from: { x: o.left + 28, y: down ? o.bottom : o.top },
    to: { x: n.left + 28, y: down ? n.top : n.bottom },
    axis: 'y',
    dir: down ? 1 : -1,
  };
}

/** The control points: a side-by-side arc rises over the higher end, a stacked one bows right. */
function controls(e: Ends): { readonly c1: Point; readonly c2: Point } {
  const { from: s, to: t } = e;
  if (e.axis === 'x') {
    const dx = t.x - s.x;
    const top = Math.max(10, Math.min(s.y, t.y) - 64 - Math.abs(t.y - s.y) * 0.15);
    return { c1: { x: s.x + dx * 0.35, y: top }, c2: { x: t.x - dx * 0.35, y: top } };
  }
  const dy = t.y - s.y;
  return { c1: { x: s.x + 150, y: s.y + dy * 0.3 }, c2: { x: t.x + 150, y: t.y - dy * 0.3 } };
}

/** The arcs a move draws: at most `TRANSFER.maxArcs`, `TRANSFER.staggerMs` apart. */
export function planArcs(
  rows: readonly TransferBox[],
  target: TransferBox | null,
  viewport: { readonly width: number; readonly height: number },
): TransferArc[] {
  const visible = onScreen(target, viewport.height);
  return rows.slice(0, TRANSFER.maxArcs).map((row, i) => {
    const ends = linkEnds(row, visible ? target : offscreenBox(row, viewport.height));
    return {
      ...ends,
      ...controls(ends),
      delayMs: i * TRANSFER.staggerMs,
      main: i === 0,
    };
  });
}

/** The point at parameter `t` on an arc's cubic bezier. */
export function arcPoint(a: TransferArc, t: number): Point {
  const u = 1 - t;
  const w0 = u * u * u;
  const w1 = 3 * u * u * t;
  const w2 = 3 * u * t * t;
  const w3 = t * t * t;
  return {
    x: w0 * a.from.x + w1 * a.c1.x + w2 * a.c2.x + w3 * a.to.x,
    y: w0 * a.from.y + w1 * a.c1.y + w2 * a.c2.y + w3 * a.to.y,
  };
}

/** The arc as an SVG path. */
function arcPath(a: TransferArc): string {
  return `M${a.from.x} ${a.from.y} C${a.c1.x} ${a.c1.y} ${a.c2.x} ${a.c2.y} ${a.to.x} ${a.to.y}`;
}

/** Cubic in-out, the packet's pace along its arc. */
function inOutCubic(q: number): number {
  return q < 0.5 ? 4 * q * q * q : 1 - (-2 * q + 2) ** 3 / 2;
}

/** A repeatable jitter in [-0.5, 0.5) for the n-th spark, so a replay draws the same sparks. */
function jitter(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s) - 0.5;
}

// ─── The player ──────────────────────────────────────────────────────────────

/** One play's bookkeeping: everything it started, so dispose can undo all of it. */
type Run = {
  readonly seam: MotionSeam;
  readonly handles: MotionHandle[];
  readonly frames: Set<number>;
  readonly layer: HTMLElement;
  readonly svg: SVGSVGElement;
  disposed: boolean;
  timer: unknown;
  sparks: number;
  /** Told once, when the play ends: its layer removed, by its timer or by a dispose. */
  readonly onClosed: () => void;
};

function play(run: Run, el: Element, frames: Keyframe[], opts: KeyframeAnimationOptions): void {
  run.handles.push(run.seam.animate(el, frames, { fill: 'both', ...opts }));
}

function svgEl(tag: string, attrs: Record<string, string | number>): SVGElement {
  const el = document.createElementNS(SVG_NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

function div(cls: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = cls;
  return el;
}

/** Places a fixed element over a box, grown by `pad` px on every side. */
function place(el: HTMLElement, box: TransferBox, pad = 0): void {
  el.style.left = `${box.left - pad}px`;
  el.style.top = `${box.top - pad}px`;
  el.style.width = `${box.width + pad * 2}px`;
  el.style.height = `${box.height + pad * 2}px`;
}

function openLayer(
  seam: MotionSeam,
  viewport: TransferSpec['viewport'],
  onClosed: () => void,
): Run {
  const layer = div('move-fx');
  layer.setAttribute('aria-hidden', 'true');
  // Inert, as well as hidden (#6772 review round 7): the echoes are clones of real rows, buttons
  // and links included, and nothing in the effects layer may take focus or a click.
  layer.setAttribute('inert', '');
  const svg = svgEl('svg', {
    class: 'move-fx__svg',
    width: viewport.width,
    height: viewport.height,
    viewBox: `0 0 ${viewport.width} ${viewport.height}`,
  }) as SVGSVGElement;
  layer.appendChild(svg);
  seam.root().appendChild(layer);
  return {
    seam,
    handles: [],
    frames: new Set(),
    layer,
    svg,
    disposed: false,
    timer: null,
    sparks: 0,
    onClosed,
  };
}

function close(run: Run): void {
  if (run.disposed) return;
  run.disposed = true;
  for (const h of run.handles) h.cancel();
  run.handles.length = 0;
  for (const f of run.frames) run.seam.cancelFrame(f);
  run.frames.clear();
  if (run.timer !== null) run.seam.clear(run.timer);
  run.layer.remove();
  run.onClosed();
}

/** The split text shadow at `px`, on the named split tokens (or clear at 0). */
function split(px: number): string {
  if (px === 0) return '0px 0 transparent, 0px 0 transparent';
  return `${-px}px 0 var(--move-fx-split-a), ${px}px 0 var(--move-fx-split-b)`;
}

/** Step 1: the row's echo brightens, splits, nudges and clips away (prototype `:2987-3002`). */
function dissolve(run: Run, node: Element, rect: TransferBox, a: TransferArc): void {
  const echo = div('move-fx__echo move-fx__echo--live');
  place(echo, rect);
  const clone = node.cloneNode(true) as Element;
  clone.removeAttribute('id');
  for (const d of clone.querySelectorAll('[id]')) d.removeAttribute('id');
  echo.appendChild(clone);
  run.layer.appendChild(echo);
  const gone =
    a.axis === 'x'
      ? a.dir > 0
        ? 'inset(0 0 0 100%)'
        : 'inset(0 100% 0 0)'
      : a.dir > 0
        ? 'inset(100% 0 0 0)'
        : 'inset(0 0 100% 0)';
  const sx = a.axis === 'x' ? a.dir : 0;
  const d = TRANSFER.dissolve;
  const full = 'inset(0 0 0 0)';
  play(
    run,
    echo,
    [
      {
        opacity: 1,
        clipPath: full,
        filter: 'brightness(1)',
        textShadow: split(0),
        transform: 'translateX(0)',
      },
      {
        opacity: 1,
        clipPath: full,
        filter: 'brightness(1.6)',
        textShadow: split(2),
        transform: `translateX(${sx * 2}px)`,
        offset: 0.18,
      },
      {
        opacity: 0.55,
        clipPath: full,
        filter: 'brightness(1.8)',
        textShadow: split(3),
        transform: `translateX(${-sx}px)`,
        offset: 0.28,
      },
      {
        opacity: 0,
        clipPath: gone,
        filter: `brightness(${d.brightness})`,
        textShadow: split(d.splitPx),
        transform: `translateX(${sx * d.nudgePx}px)`,
      },
    ],
    { duration: d.ms, delay: a.delayMs, easing: d.ease },
  );
}

/** Step 2's strokes: glow, core, hot line; the dash draws in, holds, then retracts. */
function strokes(run: Run, a: TransferArc): void {
  const d = arcPath(a);
  const layers: readonly [string, number, number][] = [
    ['move-fx__beam--live', a.main ? 7 : 5, 0.4],
    ['move-fx__core--live', a.main ? 1.6 : 1.1, 1],
    ['move-fx__hot', a.main ? 0.8 : 0.6, 0.75],
  ];
  for (const [cls, width, op] of layers) {
    const path = svgEl('path', {
      class: cls,
      d,
      fill: 'none',
      'stroke-width': width,
      'stroke-linecap': 'round',
      pathLength: 100,
      'stroke-dasharray': '100 100',
    });
    run.svg.appendChild(path);
    play(
      run,
      path,
      [
        { strokeDashoffset: 100, opacity: op },
        { strokeDashoffset: 0, opacity: op, offset: 0.45 },
        { strokeDashoffset: 0, opacity: op, offset: 0.62 },
        { strokeDashoffset: -100, opacity: 0 },
      ],
      { duration: TRANSFER.arc.ms, delay: a.delayMs, easing: TRANSFER.arc.ease },
    );
  }
}

function spark(run: Run, p: Point): void {
  run.sparks += 1;
  const n = run.sparks;
  const hot = jitter(n * 7) < -0.15;
  const c = svgEl('circle', {
    class: hot ? 'move-fx__spark move-fx__spark--hot' : 'move-fx__spark move-fx__spark--live',
    cx: (p.x + jitter(n) * 4).toFixed(1),
    cy: (p.y + jitter(n + 1) * 4).toFixed(1),
    r: (1.25 + jitter(n + 2) * 1.3).toFixed(2),
  });
  run.svg.appendChild(c);
  const tx = (jitter(n + 3) * 16).toFixed(1);
  const ty = (jitter(n + 4) * 16).toFixed(1);
  play(
    run,
    c,
    [
      { transform: 'translate(0,0) scale(1)', opacity: 0.95 },
      { transform: `translate(${tx}px,${ty}px) scale(0.2)`, opacity: 0 },
    ],
    { duration: 500 + jitter(n + 5) * 240, easing: EASE_OUT },
  );
}

function rings(run: Run, p: Point): void {
  for (const delay of [0, TRANSFER.secondRingMs]) {
    const c = svgEl('circle', { class: 'move-fx__ring-pulse', cx: p.x, cy: p.y, r: 4 });
    run.svg.appendChild(c);
    play(
      run,
      c,
      [
        { transform: 'scale(1)', opacity: 0.9 },
        { transform: 'scale(6)', opacity: 0 },
      ],
      { duration: TRANSFER.ringMs, delay, easing: EASE_OUT },
    );
  }
}

/** Step 2's packet: runs the curve, shedding sparks, and lands in two rings. */
function packet(run: Run, a: TransferArc): void {
  const halo = svgEl('circle', { class: 'move-fx__halo--live', r: a.main ? 10 : 7, opacity: 0 });
  const orb = svgEl('circle', { class: 'move-fx__packet', r: a.main ? 3.2 : 2.4, opacity: 0 });
  run.svg.append(halo, orb);
  const t0 = run.seam.now() + a.delayMs;
  let last = Number.NEGATIVE_INFINITY;
  const step = (): void => {
    run.frames.delete(handle);
    if (run.disposed) return;
    const t = run.seam.now();
    const q = (t - t0) / TRANSFER.packetMs;
    if (q >= 1) {
      orb.remove();
      halo.remove();
      rings(run, a.to);
      return;
    }
    if (q >= 0) {
      const p = arcPoint(a, inOutCubic(q));
      for (const [el, op] of [
        [orb, 1],
        [halo, 0.5],
      ] as const) {
        el.setAttribute('cx', String(p.x));
        el.setAttribute('cy', String(p.y));
        el.setAttribute('opacity', String(op));
      }
      if (t - last > TRANSFER.sparkEveryMs) {
        last = t;
        spark(run, p);
        if (a.main) spark(run, p);
      }
    }
    handle = run.seam.frame(step);
    run.frames.add(handle);
  };
  let handle = run.seam.frame(step);
  run.frames.add(handle);
}

/** Step 3: the chip naming an off-screen target flies along the main arc (prototype `:3045-3050`). */
function chip(run: Run, a: TransferArc, text: string, viewportHeight: number): void {
  const el = div('move-fx__chip--live');
  el.textContent = text;
  el.style.left = `${a.from.x}px`;
  el.style.top = `${a.from.y}px`;
  run.layer.appendChild(el);
  const dx = a.to.x - a.from.x;
  const dy = Math.min(a.to.y, viewportHeight - 24) - a.from.y;
  play(
    run,
    el,
    [
      { transform: 'translate(-50%,-50%) scale(0.7)', opacity: 0 },
      {
        transform: `translate(-50%,-50%) translate(${dx * 0.25}px,${dy * 0.25}px) scale(1)`,
        opacity: 1,
        offset: 0.25,
      },
      { transform: `translate(-50%,-50%) translate(${dx}px,${dy}px) scale(0.9)`, opacity: 0 },
    ],
    { duration: TRANSFER.chipMs, delay: a.delayMs, easing: EASE_SWEEP },
  );
}

/** Step 4's brackets: four corners 7 px outside the card, and the tag. */
function brackets(run: Run, box: TransferBox, tag: string): void {
  const hud = div('move-fx__brackets');
  place(hud, box, 7);
  for (const corner of ['tl', 'tr', 'bl', 'br']) {
    hud.appendChild(div(`move-fx__bracket--live move-fx__bracket--${corner}`));
  }
  const label = document.createElement('span');
  label.className = 'move-fx__tag--live';
  label.textContent = tag;
  hud.appendChild(label);
  run.layer.appendChild(hud);
  play(
    run,
    hud,
    [
      { opacity: 0, transform: 'scale(1.035)' },
      { opacity: 1, transform: 'scale(1)', offset: 0.28 },
      { opacity: 1, transform: 'scale(1)', offset: 0.78 },
      { opacity: 0, transform: 'scale(1)' },
    ],
    { duration: TRANSFER.brackets.ms, delay: TRANSFER.brackets.fromMs, easing: EASE_OUT },
  );
}

/** Step 4's scan line, top to bottom, and the glow. */
function scanAndGlow(run: Run, box: TransferBox): void {
  const scan = div('move-fx__scan');
  place(scan, box);
  const line = div('move-fx__scan-line--live');
  scan.appendChild(line);
  const glow = div('move-fx__glow--live');
  place(glow, box);
  run.layer.append(glow, scan);
  const h = box.height;
  play(
    run,
    line,
    [
      { transform: 'translateY(-90px)', opacity: 0 },
      { transform: `translateY(${h * 0.12}px)`, opacity: 1, offset: 0.15 },
      { transform: `translateY(${h}px)`, opacity: 1, offset: 0.9 },
      { transform: `translateY(${h}px)`, opacity: 0 },
    ],
    { duration: TRANSFER.scanMs, delay: TRANSFER.arriveMs, easing: EASE_SWEEP },
  );
  play(run, glow, [{ opacity: 0 }, { opacity: 1, offset: 0.3 }, { opacity: 0 }], {
    duration: TRANSFER.glowMs,
    delay: TRANSFER.arriveMs,
    easing: EASE_OUT,
  });
}

/** Step 4's pop on the target's job and epic counts. */
function popCounts(run: Run, target: Element): void {
  const { ms, afterMs, scale } = TRANSFER.pop;
  for (const el of target.querySelectorAll('.mission-jobs__count, .mission-zone__count')) {
    play(
      run,
      el,
      [
        { transform: 'scale(1)' },
        { transform: `scale(${scale})`, offset: 0.35 },
        { transform: 'scale(1)' },
      ],
      { duration: ms, delay: TRANSFER.arriveMs + afterMs, easing: EASE_OUT, fill: 'none' },
    );
  }
}

/** Step 5: the source card's pulse. */
function sourcePulse(run: Run, source: Element): void {
  const ring = div('move-fx__source-pulse');
  place(ring, source.getBoundingClientRect());
  run.layer.appendChild(ring);
  play(run, ring, [{ opacity: 0 }, { opacity: 1, offset: 0.3 }, { opacity: 0 }], {
    duration: TRANSFER.sourcePulseMs,
    easing: EASE_OUT,
  });
}

/**
 * Plays the transfer for an ACCEPTED move. Call it synchronously on the answer; under
 * reduced motion it draws nothing and returns a no-op. `onClosed` is told once, when the play
 * ends (at once for the no-op), so whoever holds the handle can let go of it.
 */
export function playTransfer(
  spec: TransferSpec,
  seam: MotionSeam,
  onClosed: () => void = () => {},
): TransferPlay {
  if (seam.reduced() || spec.rows.length === 0) {
    onClosed();
    return NOOP;
  }
  const run = openLayer(seam, spec.viewport, onClosed);
  // No target element is off screen too, whatever its rect says: nothing there to land on.
  const visible = spec.target !== null && onScreen(spec.targetRect, spec.viewport.height);
  const arcs = planArcs(
    spec.rows.map((r) => r.rect),
    visible ? spec.targetRect : null,
    spec.viewport,
  );
  arcs.forEach((a, i) => {
    const row = spec.rows[i];
    if (row?.node) dissolve(run, row.node, row.rect, a);
    strokes(run, a);
    packet(run, a);
  });
  const first = arcs[0];
  if (!visible && first !== undefined) chip(run, first, spec.chip, spec.viewport.height);
  if (spec.source !== null) sourcePulse(run, spec.source);
  if (visible && spec.target !== null && spec.targetRect !== null) {
    brackets(run, spec.targetRect, spec.tag);
    scanAndGlow(run, spec.targetRect);
    popCounts(run, spec.target);
  }
  run.timer = seam.later(() => close(run), TRANSFER.layerMs);
  return { dispose: () => close(run) };
}
